// src\components\AiDashboard\AiMyNutritionStatus\AiUser\AiUserSettings\nutritionTargetsCalculations.ts
import type {
  NutritionTargetsSettingsSnapshot,
  NnrReferenceSnapshot,
  DailyIntakeDocumentLike,
} from "./nutritionTargetsSettings";

/**
 * Options for building the nutrition calculations preview.
 */
export interface NutritionTargetsCalculationsBuilderOptions {
  targetsSnapshot: NutritionTargetsSettingsSnapshot | null;
  useDailySnapshot: NnrReferenceSnapshot | null;
  activeDateIso: string | null;
  rangeKind: string;
  rangeStartDateIso: string | null;
  rangeEndDateIso: string | null;
}

/**
 * Shape of the Nutrition Calculate JSON (right pane in dev modal).
 * This compares UseDaily targets vs actual intake over the selected date range.
 */
export interface NutritionTargetsCalculationsSnapshot {
  source: "nutritionTargetsCalculations";
  version: string;
  generatedAtIso: string;

  rangeInfo: {
    kind: string;
    activeDateIso: string | null;
    startDateIso: string | null;
    endDateIso: string | null;
    daysInRange: number;
  };

  targetsReference: {
    source: string | null;
    lifeStageId: string | null;
    lifeStageLabel: string | null;
  };

  intakeSummary: {
    datesWithData: string[];
    totalDaysLogged: number;
    averageDailyIntake: Record<string, number>;
    comparisonVsTargets: Record<
      string,
      {
        target: number | null;
        actual: number | null;
        percentOfTarget: number | null;
      }
    >;
  };

  rawDailyIntakeByDate: Record<string, DailyIntakeDocumentLike> | null;
}

/**
 * Helper to calculate average daily intake across multiple days.
 */
function calculateAverageDailyIntake(
  dailyIntakeByDate: Record<string, DailyIntakeDocumentLike> | null | undefined,
  startDateIso: string | null,
  endDateIso: string | null
): { datesWithData: string[]; averages: Record<string, number> } {
  if (!dailyIntakeByDate || typeof dailyIntakeByDate !== "object") {
    return { datesWithData: [], averages: {} };
  }

  const datesInRange: string[] = [];

  if (startDateIso && endDateIso) {
    const start = new Date(startDateIso);
    const end = new Date(endDateIso);

    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      const current = new Date(start);
      while (current <= end) {
        const isoDate = current.toISOString().split("T")[0];
        datesInRange.push(isoDate);
        current.setDate(current.getDate() + 1);
      }
    }
  }

  const datesWithData: string[] = [];
  const nutrientSums: Record<string, number> = {};
  const nutrientCounts: Record<string, number> = {};

  for (const dateIso of datesInRange) {
    const dayDoc = dailyIntakeByDate[dateIso];
    if (!dayDoc || !dayDoc.totals) {
      continue;
    }

    datesWithData.push(dateIso);

    for (const [nutrientKey, value] of Object.entries(dayDoc.totals)) {
      if (typeof value === "number" && Number.isFinite(value)) {
        nutrientSums[nutrientKey] = (nutrientSums[nutrientKey] || 0) + value;
        nutrientCounts[nutrientKey] = (nutrientCounts[nutrientKey] || 0) + 1;
      }
    }
  }

  const averages: Record<string, number> = {};
  for (const nutrientKey of Object.keys(nutrientSums)) {
    const count = nutrientCounts[nutrientKey] || 1;
    averages[nutrientKey] = nutrientSums[nutrientKey] / count;
  }

  return { datesWithData, averages };
}

/**
 * Try to read the user's weight (kg) from the NutritionTargetsSettings snapshot.
 */
function getUserWeightKgFromTargetsSnapshot(
  targetsSnapshot: NutritionTargetsSettingsSnapshot | null
): number | null {
  const weightFromBody =
    typeof targetsSnapshot?.body?.weight?.kilograms === "number"
      ? targetsSnapshot.body.weight.kilograms
      : null;

  if (weightFromBody != null && Number.isFinite(weightFromBody)) {
    return weightFromBody;
  }

  const weightFromRaw =
    typeof targetsSnapshot?.rawProfile?.weightKg === "number"
      ? targetsSnapshot.rawProfile.weightKg
      : null;

  if (weightFromRaw != null && Number.isFinite(weightFromRaw)) {
    return weightFromRaw;
  }

  return null;
}

/**
 * Map the app's physicalActivityLevelId to a PAL value.
 * This is intentionally small, but driven by ids, not user hard-coded values.
 */
function mapPhysicalActivityLevelIdToPal(
  physicalActivityLevelId: string | null | undefined
): number {
  const mapping: Record<string, number> = {
    low: 1.4,
    medium: 1.6,
    high: 1.8,
  };

  if (!physicalActivityLevelId) {
    return 1.4;
  }

  return mapping[physicalActivityLevelId] ?? 1.4;
}

/**
 * Get an energy requirement (EER) in MJ/day from the NNR life stage and user activity.
 */
function getEnergyRequirementMjPerDay(
  lifeStage: Record<string, unknown> | null | undefined,
  targetsSnapshot: NutritionTargetsSettingsSnapshot | null
): number | null {
  if (!lifeStage || typeof lifeStage !== "object") {
    return null;
  }

  const energy: any = (lifeStage as any).energy;
  if (!energy || typeof energy !== "object") {
    return null;
  }

  const eerByPal: Record<string, number> | undefined = energy.EER_MJd_byPAL;
  if (!eerByPal || typeof eerByPal !== "object") {
    return null;
  }

  const physicalActivityLevelId =
    targetsSnapshot?.activity?.physicalActivityLevelId ?? null;
  const pal = mapPhysicalActivityLevelIdToPal(physicalActivityLevelId);
  const palKey = pal.toFixed(1); // e.g., "1.4"

  if (typeof eerByPal[palKey] === "number") {
    return eerByPal[palKey];
  }

  // Fallback: if the exact PAL is not present, pick the first available.
  const firstKey = Object.keys(eerByPal)[0];
  if (firstKey && typeof eerByPal[firstKey] === "number") {
    return eerByPal[firstKey];
  }

  return null;
}


function buildDailyIntakeKeyFromNnr(
  nutrientId: string,
  unit: string
): string {
  if (nutrientId === "niacin") {
    return "niacin_NE";
  }

  if (nutrientId === "thiamin") {
    return "thiamin_mg";
  }

  if (nutrientId === "sodium") {
    return "sodium_g";
  }

  let suffix = unit.trim();

  suffix = suffix.replace(/\s+/g, "_");
  suffix = suffix.replace(/a-TE/gi, "aTE");
  suffix = suffix.replace(/μ/g, "µ"); 

  suffix = suffix.replace(/\/MJ/gi, "");
  suffix = suffix.replace(/\/MJ/gi, "");

  return `${nutrientId}_${suffix}`;
}


function extractNutrientTargetsFromLifeStage(
  lifeStage: Record<string, unknown> | null | undefined,
  targetsSnapshot: NutritionTargetsSettingsSnapshot | null
): Record<string, number> {
  if (!lifeStage || typeof lifeStage !== "object") {
    return {};
  }

  const nutrients: any = (lifeStage as any).nutrients;
  if (!nutrients || typeof nutrients !== "object") {
    return {};
  }

  const targets: Record<string, number> = {};

  const energyMjPerDay = getEnergyRequirementMjPerDay(lifeStage, targetsSnapshot);

  for (const [nnrNutrientId, rawEntry] of Object.entries(nutrients)) {
    if (!rawEntry || typeof rawEntry !== "object") {
      continue;
    }

    const entry: any = rawEntry;
    const unit: string = typeof entry.unit === "string" ? entry.unit : "";
    const key = buildDailyIntakeKeyFromNnr(nnrNutrientId, unit);

    // Priority: RI > AI > CDRR, then per-MJ.
    if (typeof entry.RI === "number" && Number.isFinite(entry.RI)) {
      targets[key] = entry.RI;
      continue;
    }

    if (typeof entry.AI === "number" && Number.isFinite(entry.AI)) {
      targets[key] = entry.AI;
      continue;
    }

    if (typeof entry.CDRR === "number" && Number.isFinite(entry.CDRR)) {
      targets[key] = entry.CDRR;
      continue;
    }

    // Energy-normalised nutrients: thiamin, niacin.
    if (
      nnrNutrientId === "thiamin" &&
      typeof entry.RI_per_MJ === "number" &&
      Number.isFinite(entry.RI_per_MJ) &&
      typeof energyMjPerDay === "number"
    ) {
      targets[key] = entry.RI_per_MJ * energyMjPerDay;
      continue;
    }

    if (
      nnrNutrientId === "niacin" &&
      typeof entry.RI_per_MJ_NE === "number" &&
      Number.isFinite(entry.RI_per_MJ_NE) &&
      typeof energyMjPerDay === "number"
    ) {
      targets[key] = entry.RI_per_MJ_NE * energyMjPerDay;
      continue;
    }
  }

  return targets;
}

/**
 * Build additional targets (energy, protein, fibre, carbs, fat) from NNR life stage and user profile.
 *
 * Returns keys that match Firestore totals:
 * - energy_kcal, energy_kj
 * - protein_g (RI by kg)
 * - fibre_g (min per MJ)
 * - carbohydrates_g (midpoint of recommended E% band)
 * - fat_g (midpoint of recommended E% band)
 */
function buildMacroAndEnergyTargets(
  lifeStage: Record<string, unknown> | null | undefined,
  targetsSnapshot: NutritionTargetsSettingsSnapshot | null
): Record<string, number> {
  if (!lifeStage || typeof lifeStage !== "object") {
    return {};
  }

  const targets: Record<string, number> = {};

  const energyMjPerDay = getEnergyRequirementMjPerDay(lifeStage, targetsSnapshot);
  const userWeightKg = getUserWeightKgFromTargetsSnapshot(targetsSnapshot);

  if (typeof energyMjPerDay === "number") {
    const energyKjPerDay = energyMjPerDay * 1000;
    const energyKcalPerDay = energyMjPerDay * 239.005736;

    targets.energy_kj = energyKjPerDay;
    targets.energy_kcal = energyKcalPerDay;

    const macros: any = (lifeStage as any).macros;
    const fibrePerMjMin: number | null =
      typeof macros?.fibrePerMJ_g?.min === "number"
        ? macros.fibrePerMJ_g.min
        : null;

    if (fibrePerMjMin != null && Number.isFinite(fibrePerMjMin)) {
      targets.fibre_g = fibrePerMjMin * energyMjPerDay;
    }

    // Derive carb and fat gram targets from E% ranges using the midpoint of each band.
    const ranges: any = macros?.ranges_Epct;
    const carbRange = ranges?.carbs;
    const fatRange = ranges?.fat;

    const carbMin =
      typeof carbRange?.min === "number" && Number.isFinite(carbRange.min)
        ? carbRange.min
        : null;
    const carbMax =
      typeof carbRange?.max === "number" && Number.isFinite(carbRange.max)
        ? carbRange.max
        : null;

    if (carbMin !== null && carbMax !== null) {
      const carbMidPct = (carbMin + carbMax) / 2;
      const carbKcalPerDay = (carbMidPct / 100) * energyKcalPerDay;
      // 4 kcal per gram of carbohydrate
      targets.carbohydrates_g = carbKcalPerDay / 4;
    }

    const fatMin =
      typeof fatRange?.min === "number" && Number.isFinite(fatRange.min)
        ? fatRange.min
        : null;
    const fatMax =
      typeof fatRange?.max === "number" && Number.isFinite(fatRange.max)
        ? fatRange.max
        : null;

    if (fatMin !== null && fatMax !== null) {
      const fatMidPct = (fatMin + fatMax) / 2;
      const fatKcalPerDay = (fatMidPct / 100) * energyKcalPerDay;
      // 9 kcal per gram of fat
      targets.fat_g = fatKcalPerDay / 9;
    }
  }

  const proteinByKg: any = (lifeStage as any).proteinByKg;
  if (
    typeof proteinByKg?.RI_g_per_kg === "number" &&
    Number.isFinite(proteinByKg.RI_g_per_kg) &&
    typeof userWeightKg === "number" &&
    Number.isFinite(userWeightKg)
  ) {
    targets.protein_g = proteinByKg.RI_g_per_kg * userWeightKg;
  }

  return targets;
}

/**
 * Build the nutrition calculations snapshot comparing targets vs actual intake.
 */
export function buildNutritionTargetsCalculationsPreview(
  options: NutritionTargetsCalculationsBuilderOptions
): NutritionTargetsCalculationsSnapshot {
  const {
    targetsSnapshot,
    useDailySnapshot,
    activeDateIso,
    rangeKind,
    rangeStartDateIso,
    rangeEndDateIso,
  } = options;

  const dailyIntakeByDate = targetsSnapshot?.dailyIntakeByDate || null;

  // Calculate average intake over the selected range.
  const { datesWithData, averages } = calculateAverageDailyIntake(
    dailyIntakeByDate,
    rangeStartDateIso,
    rangeEndDateIso
  );

  // Extract targets from NNR life stage (micronutrients + macros/energy).
  const lifeStage = useDailySnapshot?.lifeStage || null;
  const micronutrientTargets = extractNutrientTargetsFromLifeStage(
    lifeStage,
    targetsSnapshot
  );
  const macroAndEnergyTargets = buildMacroAndEnergyTargets(
    lifeStage,
    targetsSnapshot
  );

  const nutrientTargets: Record<string, number> = {
    ...micronutrientTargets,
    ...macroAndEnergyTargets,
  };

  // Build comparison: target vs actual vs % of target.
  const comparisonVsTargets: Record<
    string,
    {
      target: number | null;
      actual: number | null;
      percentOfTarget: number | null;
    }
  > = {};

  // All nutrients that appear in either targets or actuals.
  const allNutrientKeys = new Set([
    ...Object.keys(nutrientTargets),
    ...Object.keys(averages),
  ]);

  for (const nutrientKey of allNutrientKeys) {
    const target =
      typeof nutrientTargets[nutrientKey] === "number"
        ? nutrientTargets[nutrientKey]
        : null;
    const actual =
      typeof averages[nutrientKey] === "number" ? averages[nutrientKey] : null;

    let percentOfTarget: number | null = null;
    if (target !== null && target > 0 && actual !== null) {
      percentOfTarget = Math.round((actual / target) * 100);
    }

    comparisonVsTargets[nutrientKey] = {
      target,
      actual,
      percentOfTarget,
    };
  }

  // Calculate days in range.
  let daysInRange = 0;
  if (rangeStartDateIso && rangeEndDateIso) {
    const start = new Date(rangeStartDateIso);
    const end = new Date(rangeEndDateIso);
    if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime())) {
      daysInRange =
        Math.ceil(
          (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
        ) + 1;
    }
  }

  return {
    source: "nutritionTargetsCalculations",
    version: "2024-01-01",
    generatedAtIso: new Date().toISOString(),

    rangeInfo: {
      kind: rangeKind,
      activeDateIso,
      startDateIso: rangeStartDateIso,
      endDateIso: rangeEndDateIso,
      daysInRange,
    },

    targetsReference: {
      source: useDailySnapshot?.source || null,
      lifeStageId: useDailySnapshot?.lifeStageId || null,
      lifeStageLabel: useDailySnapshot?.lifeStageLabel || null,
    },

    intakeSummary: {
      datesWithData,
      totalDaysLogged: datesWithData.length,
      averageDailyIntake: averages,
      comparisonVsTargets,
    },

    rawDailyIntakeByDate: dailyIntakeByDate,
  };
}
